import { getCurrentInstance } from 'vue';
import {
	type RetainerPeriod,
	type Contact,
	type Project,
	type SelfServiceDataInput,
	type Task,
	type CalendarEvent,
	type ProjectPhase,
	type ActivityType,
	type Quote,
	type TimeEntry,
	type User,
	type Role,
} from '@/generated/graphql';
import { type Constants } from '@/constants';

export type ShowEventPopupPayload = {
	eventType: 'cal' | 'task';
	eventId?: Task['id'] | CalendarEvent['id'];
	parent_id?: Task['id'];
	project_id?: Project['id'];
	project_phase_id?: ProjectPhase['id'];
	company_id?: Contact['id'];
	person_id?: Contact['id'];
	retainer_period_id?: RetainerPeriod['id'];
	activity_id?: ActivityType['id'];
	quote_id?: Quote['id'];
};

export type RootEvents = {
	'show-send-mail-modal':
		| {
				module: typeof Constants.Module.COMPANIES.name;
				companyId?: Contact['id'];
				vertical?: 'center';
				objectId?: number;
				email?: string;
		  }
		| {
				module: typeof Constants.Module.PERSONS.name;
				personId: Contact['id'];
				vertical?: 'center';
				objectId?: number;
				email?: string;
		  }
		| {
				module: typeof Constants.Module.CONTACTS.name;
				personId?: Contact['id'];
				vertical?: 'center';
				objectId?: number;
				email?: string;
		  };
	'show-event-popup': ShowEventPopupPayload;
	'init-monolith-selectors': {
		baseSelector: string;
	};
	'init-tinymce': {
		baseSelector: string;
	};
	'fill-timetracker-dropdown': undefined;
	'fill-notifications-dropdown': undefined;
	'projects-form-changed': undefined;
	'quotes-form-changed': undefined;
	'open-self-service-detailed-breakdown': SelfServiceDataInput;
	'start-page-tour': undefined;
	'open-schedule-demo': {
		firstname: string;
		lastname: string;
		company: string;
		email: string;
		phone: string;
		companysize: string;
	};
	'open-self-service': undefined;
	'cancel-self-service-subscription': undefined;
	'open-self-service-set-up-payment': {
		userChanges: SelfServiceDataInput;
		stateHasChanged: boolean | undefined;
	};
	'retrospective-linking-submission': undefined;
	're-focus-time-tracker-search': undefined;
	'reload-time-tracker-component': undefined;
	'local-price-list-saved': { id: string };
	'delete-demo-data-from-dropdown': undefined;
	'delete-demo-data-from-trial-guide': undefined;
	'update-project-end-date': { endDate: string };
	'project-type-changed': { projectType: string };
	'share-snapshot-modal-closed': undefined;
	'version-updates-modal-seen': undefined;
	'project-status-completed': { projectId: string };
	'auto-suggest-cleared': undefined;
	'auto-suggest-selected': { id: string | number; name: string };
	'event-dates-changed': {
		startDateTime: string;
	};
	're-fetch-free-time-slots': undefined;
	'version-popup': undefined;
	'start-time-tracker': {
		taskId: Task['id'];
		completePreviousTracker?: {
			timeEntryId: TimeEntry['id'];
			openModalOnly: boolean;
		};
	};
	'complete-active-time-tracker': {
		timeEntryId: TimeEntry['id'];
		openModalOnly: boolean;
	};
	'modify-time-entry': {
		taskId: Task['id'];
		duration: number;
		isCompleted: boolean;
		timeEntryId?: TimeEntry['id'];
	};
	'quote-task-owner-changed': {
		ownerId: string | null;
	};
	'quote-task-assignees-changed': {
		userIds: User['id'][];
		roleIds: Role['id'][];
		shouldNotify: boolean;
		userAllocations?: Array<{ userId: User['id']; duration: number }>;
		roleAllocations?: Array<{ roleId: Role['id']; duration: number }>;
		hasCustomAllocation?: boolean;
	};
	'quote-task-allocated-times-changed': {
		userAllocations: Array<{ userId: User['id']; duration: number }>;
		roleAllocations: Array<{ roleId: Role['id']; duration: number }>;
	};
	'quote-task-allocation-mode-changed': {
		hasCustomAllocation: boolean;
	};
	'user-event-assign-selection-changed': {
		userIds: User['id'][];
		roleIds: Role['id'][];
		hasIndividualAllocation: boolean;
	};
};

export type RootEmit = <T extends keyof RootEvents>(
	message: T,
	payload?: RootEvents[T]
) => void;

/**
 * A helper composable for facilitating the MFE -> monolith communication.
 */
export const useMfeUtils = () => {
	const vm = getCurrentInstance();

	/**
	 * Emit an event from the micro-frontend root so that it can easily be
	 * listened to in the monolith.
	 */
	const rootEmit: RootEmit = (message, payload) => {
		// We can't emit the event directly from the `root` because that is the
		// `new Vue` instance created in MFE core `render` function. Walk up
		// the component tree until we find the *first child* of the MFE root.
		// This is the actual component that is rendered and which has the
		// event listeners.
		let component = vm?.proxy || undefined;
		while (component?.$parent?.$parent) {
			component = component.$parent;
		}

		component?.$emit(message, payload);
	};

	const getCSRFToken = () => window.tokemon || '';

	return {
		rootEmit,
		getCSRFToken: getCSRFToken,
	};
};
